/**
 * WriterStatsDrawer — bottom-sheet drawer that renders a scribe's full
 * stats (books, series, episodes, readers, retention, achievements).
 *
 * Only Kings and the High King can open it; regular users see a plain
 * non-clickable author name. The drawer hits the existing
 * `/api/admin/users/{user_id}/profile` endpoint which was extended on
 * Feb 12 2026 to include the scribe-specific fields.
 *
 * Props:
 *   userId   (string) — the scribe's user_id
 *   open     (bool)   — controlled visibility
 *   onClose  (fn)     — close handler
 *   displayName (string?) — fallback name for the header while loading
 */
import React, { useEffect, useState } from 'react';
import {
  X, BookOpen, Headphones, Film, Layers, Users, TrendingUp,
  Award, Loader2, Trophy, Sparkles, Globe,
} from 'lucide-react';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StatTile = ({ label, value, Icon, accent = 'amber', testid }) => {
  const palette = {
    amber:   'border-amber-500/30   bg-amber-500/10   text-amber-200',
    fuchsia: 'border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-200',
    indigo:  'border-indigo-500/30  bg-indigo-500/10  text-indigo-200',
    emerald: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200',
    cyan:    'border-cyan-500/30    bg-cyan-500/10    text-cyan-200',
    pink:    'border-pink-500/30    bg-pink-500/10    text-pink-200',
  }[accent] || 'border-slate-500/30 bg-slate-500/10 text-slate-200';
  return (
    <div className={`rounded-xl border ${palette} p-3`} data-testid={testid}>
      <div className="flex items-center gap-1.5">
        <Icon className="w-3 h-3 opacity-80" />
        <p className="text-[10px] uppercase tracking-widest font-bold opacity-80">{label}</p>
      </div>
      <p className="text-2xl font-black text-white tabular-nums mt-0.5">{value ?? 0}</p>
    </div>
  );
};

const WriterStatsDrawer = ({ userId, open, onClose, displayName }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!open || !userId) return;
    let cancelled = false;
    setData(null);
    setErr('');
    setLoading(true);
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/admin/users/${userId}/profile`);
        const d = await r.json().catch(() => ({}));
        if (cancelled) return;
        if (!r.ok) {
          setErr(d.detail || 'Could not load writer stats');
          return;
        }
        setData(d);
      } catch {
        if (!cancelled) setErr('Network error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, userId]);

  if (!open) return null;

  const u = data?.user || {};
  const o = data?.output || {};
  const b = data?.behaviour || {};
  const e = data?.earnings || {};
  const name = u.name || displayName || u.email || 'Scribe';

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="writer-stats-drawer"
    >
      <div
        className="w-full sm:max-w-2xl bg-slate-950 border-t sm:border border-amber-500/30 sm:rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(ev) => ev.stopPropagation()}
      >
        <header className="sticky top-0 z-10 bg-slate-950/95 backdrop-blur border-b border-slate-800 px-5 py-4 flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-amber-500/20 border border-amber-500/40 grid place-items-center shrink-0 overflow-hidden">
            {u.picture ? (
              <img src={u.picture} alt="" className="w-full h-full object-cover" />
            ) : (
              <Sparkles className="w-5 h-5 text-amber-300" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Scribe Stats 🖋️</p>
            <h2 className="text-lg font-black text-white truncate" data-testid="writer-stats-name">{name}</h2>
            {u.country && (
              <p className="text-[11px] text-slate-400 flex items-center gap-1">
                <Globe className="w-3 h-3" /> {u.country} · {u.role}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 grid place-items-center"
            data-testid="writer-stats-close"
            aria-label="Close"
          >
            <X className="w-4 h-4 text-white" />
          </button>
        </header>

        <div className="px-5 py-4 space-y-5">
          {loading && (
            <div className="text-center text-slate-400 text-sm py-8">
              <Loader2 className="w-5 h-5 inline-block animate-spin mr-2" /> Loading…
            </div>
          )}
          {err && !loading && (
            <p className="text-sm text-rose-300" data-testid="writer-stats-error">{err}</p>
          )}
          {data && !loading && (
            <>
              {/* Published output */}
              <section data-testid="writer-stats-output">
                <h3 className="eyebrow text-amber-300 mb-2">Published output</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <StatTile label="Ebooks" value={o.ebooks} Icon={BookOpen} accent="amber" testid="writer-stats-ebooks" />
                  <StatTile label="Audiobooks" value={o.audiobook_series} Icon={Headphones} accent="fuchsia" testid="writer-stats-audiobooks" />
                  <StatTile label="Cinematic" value={o.cinematic_series} Icon={Film} accent="pink" testid="writer-stats-cinematic" />
                  <StatTile label="Book Series" value={o.text_series} Icon={Layers} accent="indigo" testid="writer-stats-text-series" />
                  <StatTile label="Text Episodes" value={o.text_episodes} Icon={Layers} accent="indigo" testid="writer-stats-text-episodes" />
                  <StatTile label="Chapters" value={o.chapters} Icon={Layers} accent="cyan" testid="writer-stats-chapters" />
                </div>
                <p className="text-[11px] text-slate-400 mt-2 tabular-nums" data-testid="writer-stats-words">
                  {o.words_published?.toLocaleString() || 0} words published
                  {o.xp_from_words ? (
                    <span className="text-amber-300/80"> · +{o.xp_from_words.toLocaleString()} Scribe XP earned 🖋️</span>
                  ) : null}
                </p>
                <p className="text-[10px] text-slate-500 mt-0.5">Rule: 100 XP per 1,000 words published.</p>
              </section>

              {/* Readership */}
              <section data-testid="writer-stats-readership">
                <h3 className="eyebrow text-cyan-300 mb-2">Readership</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <StatTile label="Readers" value={o.distinct_readers} Icon={Users} accent="cyan" testid="writer-stats-readers" />
                  <StatTile label="Followers" value={o.followers} Icon={Users} accent="emerald" testid="writer-stats-followers" />
                  <StatTile label="Retention" value={`${o.retention_pct || 0}%`} Icon={TrendingUp} accent="emerald" testid="writer-stats-retention" />
                  <StatTile label="Episode finishes" value={o.text_episode_finishes} Icon={Sparkles} accent="indigo" testid="writer-stats-episode-finishes" />
                </div>
              </section>

              {/* Reader-side behaviour */}
              <section data-testid="writer-stats-behaviour">
                <h3 className="eyebrow text-fuchsia-300 mb-2">As a reader / listener</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <StatTile label="Books opened" value={b.books_opened} Icon={BookOpen} accent="amber" testid="writer-stats-books-opened" />
                  <StatTile label="Completed" value={b.books_completed} Icon={BookOpen} accent="emerald" testid="writer-stats-books-completed" />
                  <StatTile label="Listened (min)" value={b.listening_minutes} Icon={Headphones} accent="fuchsia" testid="writer-stats-listening-min" />
                  <StatTile label="Streak best" value={b.streak_best} Icon={TrendingUp} accent="pink" testid="writer-stats-streak-best" />
                </div>
              </section>

              {/* Earnings */}
              <section data-testid="writer-stats-earnings">
                <h3 className="eyebrow text-amber-300 mb-2">Earnings</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <StatTile label="Scribe XP" value={e.writer_xp} Icon={Sparkles} accent="amber" testid="writer-stats-writer-xp" />
                  <StatTile label="Scribe level" value={e.writer_level} Icon={Award} accent="amber" testid="writer-stats-writer-level" />
                  <StatTile label="Coin balance" value={e.coin_balance} Icon={Trophy} accent="emerald" testid="writer-stats-coins" />
                  <StatTile label="Lifetime coins" value={e.lifetime_coins_earned} Icon={Trophy} accent="emerald" testid="writer-stats-lifetime-coins" />
                </div>
              </section>

              {/* Achievements */}
              {!!data.achievements?.length && (
                <section data-testid="writer-stats-achievements">
                  <h3 className="eyebrow text-pink-300 mb-2">Achievements ({data.achievements.length})</h3>
                  <div className="grid grid-cols-2 gap-1.5">
                    {data.achievements.slice(0, 24).map((a, i) => (
                      <div
                        key={a.id || i}
                        className="rounded-lg border border-pink-500/20 bg-pink-500/5 px-2 py-1.5 flex items-center gap-2"
                        data-testid={`writer-stats-achievement-${i}`}
                      >
                        <Trophy className="w-3 h-3 text-pink-300 shrink-0" />
                        <span className="text-xs text-white truncate">{a.label || a.id}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WriterStatsDrawer;
